import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput } from '@mui/material'
import { Plus } from 'lucide-react'
import { useId, useState } from 'react'
import {
  type Control,
  type FieldValues,
  type Path,
  type PathValue,
  useController,
} from 'react-hook-form'
import { ThemeColors } from '../../utils/theme/colors.theme'

interface FileInputRhfProps<T extends FieldValues> {
  control: Control<T> // Controller attribute from the form
  name: Path<T> // Field name from the form
  label: string
  accept?: string // File types accepted
  defaultName?: string
  newDesign?: boolean
}

const FileInputRhf = <T extends FieldValues>({
  label,
  control,
  name,
  accept,
  defaultName,
  newDesign = false,
}: FileInputRhfProps<T>) => {
  const [filename, setFilename] = useState<string | undefined>(undefined)
  const inputId = useId()

  const {
    field: { onChange, onBlur: rhfOnBlur },
    fieldState: { error: rhfError },
  } = useController({ control, name })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    if (files !== null && files.length > 0) {
      onChange(files[0] as PathValue<T, Path<T>>)
      setFilename(files[0].name)
    }
  }

  if (newDesign) {
    return (
      <div className="space-y-1">
        <label htmlFor={inputId} className="sr-only">
          {label}
        </label>
        <input
          id={inputId}
          type="file"
          accept={accept ?? 'image/*'}
          onBlur={rhfOnBlur}
          onChange={handleChange}
          className="w-full text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-muted/80"
          aria-invalid={rhfError !== undefined}
        />
        {rhfError && <p className="text-xs text-destructive">{rhfError.message}</p>}
      </div>
    )
  }

  return (
    <FormControl fullWidth>
      <InputLabel htmlFor={inputId} color="info">
        {label}
      </InputLabel>
      <OutlinedInput
        readOnly
        color="info"
        label={label}
        onBlur={rhfOnBlur}
        value={filename ?? defaultName ?? ''}
        error={rhfError !== undefined}
        endAdornment={
          <InputAdornment position="end">
            <IconButton component="label" htmlFor={inputId}>
              <Plus
                style={{
                  color: ThemeColors.BLACK,
                  height: '24px',
                  width: '24px',
                }}
              />
              <input
                id={inputId}
                type="file"
                accept={accept ?? 'image/*'}
                style={{ display: 'none' }}
                onChange={handleChange}
              />
            </IconButton>
          </InputAdornment>
        }
      />
    </FormControl>
  )
}

export default FileInputRhf
